import type { components } from '@/api'
import { stringIsValidTool } from '@/backend/guards'
import {
  PropertyType,
  type EntityResponse,
  type PropertyResponse,
  type ViewResponse,
} from '@/backend/types'
import { coalesceEmptyObjectToNull, getUnionProperty, stripUndefined } from '@/shared/utils/object'
import { exhaustiveGuard, invariant } from '@/shared/utils/typeAssertions'
import { captureException } from '@sentry/vue'
import type { BaseField, DefinedField, Field, NumberField, ReferenceField } from './Fields/types'
import type { BaseProperty, Property } from './Properties/types'
import { type Entity, type View } from './types'

export const serializeEntity = (response: EntityResponse): Entity =>
  stripUndefined({
    id: response.id,
    activeViewIds: response.active_view_ids,
    fields: serializeFields(response.fields),
    parentEntityId: response.parent_entity_id,
    projectId: response.project_id,
  })

export const serializeFields = (fields: EntityResponse['fields']) =>
  new Map(
    Object.values(fields)
      .filter((f): f is DefinedField => f !== undefined)
      .toSorted((a, b) => a.property_id.localeCompare(b.property_id))
      .map((field) => [field.property_id, serializeField(field)]),
  )

export const serializeField = (
  fieldResponse: components['schemas']['Projects.Entities.FieldResponse'],
): Field => {
  const toolValueUpdatedBy = stringIsValidTool(fieldResponse.tool_value.updated_by)
    ? fieldResponse.tool_value.updated_by
    : null

  const baseProps: Omit<BaseField<unknown>, 'manualValue' | 'toolValue' | 'draftValue'> & {
    draftValue: undefined
  } = {
    propertyId: fieldResponse.property_id,
    entityId: fieldResponse.entity_id,
    status: fieldResponse.status,
    staleness: fieldResponse.staleness,
    errorMessage: fieldResponse.error_message,
    propertyHash: fieldResponse.property_hash,
    groundTruth: fieldResponse.ground_truth,
    updatedAt: fieldResponse.updated_at,
    createdAt: fieldResponse.created_at,
    manualValueUpdatedBy: fieldResponse.manual_value.updated_by,
    toolValueUpdatedBy,
    score: fieldResponse.manual_metadata?.value_score ?? null,
    draftValue: undefined,
  }

  const grounding = getUnionProperty('grounding', [
    fieldResponse.tool_value,
    fieldResponse.manual_value,
  ])

  if (
    fieldResponse.property_type === PropertyType.file ||
    fieldResponse.property_type === PropertyType.data
  ) {
    const ocrPages = getUnionProperty(
      'ocr_pages',
      [fieldResponse.tool_value, fieldResponse.manual_value],
      (v) => Boolean(v && Array.isArray(v) && v.length),
    )

    const transcription = getUnionProperty('transcription', [
      fieldResponse.tool_value,
      fieldResponse.manual_value,
    ])

    const manualValue =
      fieldResponse.manual_value as components['schemas']['Projects.Entities.FieldFileResponse']

    const toolValue =
      fieldResponse.tool_value as components['schemas']['Projects.Entities.FieldFileResponse']

    return {
      type: fieldResponse.property_type,
      manualValue: manualValue.value,
      manualValuePDF: manualValue.pdf?.url ?? null,
      manualFilename: manualValue.original_filename,
      toolValue: toolValue.pdf?.url ?? toolValue.value,
      toolValuePDF: manualValue.pdf?.url ?? null,
      toolFilename: toolValue.original_filename,
      ocrPages: ocrPages ?? null,
      transcription: transcription ?? null,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.text) {
    return {
      type: 'text',
      manualValue: fieldResponse.manual_value.value as string | null,
      toolValue: fieldResponse.tool_value.value as string | null,
      grounding: grounding ?? null,
      hasGrounding:
        'has_grounding' in fieldResponse.tool_value && fieldResponse.tool_value.has_grounding,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.single_select) {
    return {
      type: 'single_select',
      manualValue: fieldResponse.manual_value.value as string[] | null,
      toolValue: fieldResponse.tool_value.value as string[] | null,
      metadata: fieldResponse.tool_metadata,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.multi_select) {
    return {
      type: 'multi_select',
      manualValue: fieldResponse.manual_value.value as string[] | null,
      toolValue: fieldResponse.tool_value.value as string[] | null,
      metadata: fieldResponse.tool_metadata,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.user_select) {
    return {
      type: 'user_select',
      manualValue: fieldResponse.manual_value.value as string[] | null,
      toolValue: fieldResponse.tool_value.value as string[] | null,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.json) {
    return {
      type: 'json',
      manualValue: fieldResponse.manual_value.value as string | null,
      toolValue: fieldResponse.tool_value.value as string | null,
      grounding: grounding ?? null,
      hasGrounding:
        'has_grounding' in fieldResponse.tool_value && fieldResponse.tool_value.has_grounding,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.url) {
    return {
      type: 'url',
      metadata: fieldResponse.tool_metadata,
      manualValue: fieldResponse.manual_value.value as string | null,
      toolValue: fieldResponse.tool_value.value as string | null,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.collection) {
    return {
      type: 'collection',
      manualValue: fieldResponse.manual_value.value as string | null,
      manualName: fieldResponse.manual_metadata?.field_name ?? null,
      toolValue: fieldResponse.tool_value.value as string | null,
      subprojectPreview: fieldResponse.subproject_preview
        ? {
            entityCount: fieldResponse.subproject_preview.total_entity_count,
            entityPreviews: fieldResponse.subproject_preview.entity_previews,
          }
        : undefined,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.file_collection) {
    return {
      type: 'file_collection',
      manualValue: fieldResponse.manual_value.value as string | string[] | null,
      toolValue: fieldResponse.tool_value.value as string | string[] | null,
      manualName: fieldResponse.manual_metadata?.field_name ?? null,
      manualFilename:
        'original_filename' in fieldResponse.manual_value
          ? fieldResponse.manual_value.original_filename
          : null,
      toolFilename:
        'original_filename' in fieldResponse.tool_value
          ? fieldResponse.tool_value.original_filename
          : null,

      subprojectPreview: fieldResponse.subproject_preview
        ? {
            entityCount: fieldResponse.subproject_preview.total_entity_count,
            entityPreviews: fieldResponse.subproject_preview.entity_previews,
          }
        : undefined,
      ...baseProps,
    }
  }

  if (fieldResponse.property_type === PropertyType.reference) {
    if (
      !('matched_entity_ids' in fieldResponse.manual_value) ||
      !('matched_entity_ids' in fieldResponse.tool_value)
    ) {
      // Our API spec isn't typed strictly enough to guarantee this, but if
      // we get here then something is very wrong and users will have a very
      // broken experience.
      captureException(
        new Error("Reference field doesn't have matched_entity_ids - something is very wrong."),
      )
    }

    const field: ReferenceField = {
      ...baseProps,
      type: 'reference',
      manualEntityIds:
        'matched_entity_ids' in fieldResponse.manual_value
          ? fieldResponse.manual_value.matched_entity_ids
          : [],
      toolEntityIds:
        'matched_entity_ids' in fieldResponse.tool_value
          ? fieldResponse.tool_value.matched_entity_ids
          : [],
      manualValue:
        'matched_entity_ids' in fieldResponse.manual_value
          ? coalesceEmptyObjectToNull(fieldResponse.manual_value.value)
          : null,
      toolValue:
        'matched_entity_ids' in fieldResponse.tool_value
          ? coalesceEmptyObjectToNull(fieldResponse.tool_value.value)
          : null,
      subprojectPreview:
        'subproject_preview' in fieldResponse ? fieldResponse.subproject_preview || null : null,
      metadata: fieldResponse.tool_metadata,
    }
    return field
  }

  if (fieldResponse.property_type === 'number') {
    let field: NumberField

    const metadata = fieldResponse.tool_metadata || null

    if (
      /**
       * This condition checks that the field response is a number field.
       *
       * Unfortunately, the TS version we use at the time of writing (5.6.3)
       * isn't clever enough to correctly infer the type if these conditions
       * are pulled into a variable.
       */
      'raw_text' in fieldResponse.manual_value &&
      fieldResponse.manual_value.value &&
      'unit' in fieldResponse.manual_value.value &&
      'raw_text' in fieldResponse.tool_value &&
      fieldResponse.tool_value.value &&
      'unit' in fieldResponse.tool_value.value
    ) {
      field = {
        ...baseProps,
        type: 'number',
        manualValue: fieldResponse.manual_value.value.number,
        manualValueUnit: fieldResponse.manual_value.value.unit,
        manualValueRaw: fieldResponse.manual_value.raw_text,
        toolValue: fieldResponse.tool_value.value.number,
        toolValueUnit: fieldResponse.tool_value.value.unit,
        toolValueRaw: fieldResponse.tool_value.raw_text,
        metadata,
        hasGrounding:
          'has_grounding' in fieldResponse.tool_value && fieldResponse.tool_value.has_grounding,
        grounding: grounding || null,
      }
    } else {
      /**
       * This is an error case. When a tool errors, the field value's will follow
       * the schema for a text field, and will be set to `null`.
       */
      field = {
        ...baseProps,
        type: 'number',
        manualValue: null,
        toolValue: null,
        manualValueUnit: null,
        toolValueUnit: null,
        manualValueRaw: null,
        toolValueRaw: null,
        metadata,
        hasGrounding: false,
      }
    }

    return field
  }

  invariant(fieldResponse.property_type !== 'hub_select', 'TODO')

  exhaustiveGuard(
    fieldResponse.property_type,
    `Unhandled field type: ${fieldResponse.property_type}`,
  )

  throw new Error('Field type could not be inferred')
}

export const serializeProperty = (payload: PropertyResponse): Property => {
  invariant(payload.type !== 'hub_select', 'TODO')

  const base: BaseProperty = {
    id: payload.id,
    name: payload.name,
    tool: payload.tool,
    isGrounded: payload.is_grounded,
    effectiveTool: payload.effective_tool,
    description: payload.description,
    inputs: payload.inputs.map((i) => ({
      entityId: i.entity_id,
      filters: i.entities_filter,
      propertyId: i.property_id,
      viaPropertyId: i.via_property_id,
    })),
    hash: payload.hash,
    slug: payload.slug,
    owner: payload.owner,
    parentProjectId: payload.parent_project_id,
    parentPropertyId: payload.parent_property_id,
    // We've received this payload from the backend, so the property will never be
    // optimistic here
    isOptimistic: false,
    toolConfig: payload.tool_config,
  }

  if (payload.type === PropertyType.single_select || payload.type === PropertyType.user_select) {
    invariant(
      'config' in payload && payload.config && 'options' in payload.config,
      'Invalid single/user select property config',
    )

    return {
      ...base,
      type: payload.type,
      config: {
        options: payload.config.options,
        defaultOption: payload.config.default_option,
      },
    }
  }

  if (payload.type === PropertyType.multi_select) {
    invariant(
      'config' in payload && payload.config && 'options' in payload.config,
      'Invalid multi select property config',
    )

    return {
      ...base,
      type: payload.type,
      config: {
        options: payload.config.options,
        maxSelected: 'max_selected' in payload.config ? payload.config.max_selected : undefined,
        defaultOption: payload.config.default_option,
      },
    }
  }

  if (payload.type === PropertyType.collection) {
    if (
      !(
        'config' in payload &&
        payload.config &&
        'properties' in payload.config &&
        payload.config.properties
      )
    ) {
      captureException(new Error('Invalid collection config'), { data: payload })
      throw new Error('Invalid collection config')
    }

    return {
      ...base,
      type: payload.type,
      config: {
        properties: payload.config.properties,
        subprojectConfig: payload.config.subproject_config,
      },
    }
  }

  if (payload.type === PropertyType.file_collection) {
    invariant(
      'config' in payload &&
        payload.config &&
        'properties' in payload.config &&
        payload.config.properties &&
        'pdf_split_mode' in payload.config &&
        'pdf_split_size' in payload.config,
      'Invalid file_collection config',
    )
    return {
      ...base,
      type: payload.type,
      config: {
        properties: payload.config.properties,
        subprojectConfig: payload.config.subproject_config,
        pdfSplitMode: payload.config.pdf_split_mode,
        pdfSplitSize: payload.config.pdf_split_size,
      },
    }
  }

  if (payload.type === PropertyType.reference) {
    if (
      !payload.config ||
      !('entity_limit' in payload.config) ||
      !('project_id' in payload.config)
    ) {
      throw new Error(
        "Invalid reference property config. 'entity_limit' and 'project_id' are required",
      )
    }

    return {
      ...base,
      type: payload.type,
      config: {
        entityLimit: payload.config.entity_limit,
        projectId: payload.config.project_id,
      },
    }
  }

  if (payload.type === PropertyType.number) {
    invariant(
      'config' in payload && payload.config && 'format' in payload.config,
      'Invalid number property config',
    )

    return {
      ...base,
      type: payload.type,
      config: {
        isCustomFormat: payload.config.format === 'custom',
        format: {
          decimalPlaces: payload.config.format_custom?.decimal_places ?? 'auto',
          negativeFormat: payload.config.format_custom?.negative_format || 'minus',
          rightAlign: payload.config.format_custom?.right_align || false,
          thousandsSeparator: payload.config.format_custom?.thousand_separator === 'comma',
        },
      },
    }
  }

  return {
    ...base,
    type: payload.type,
  }
}

export const serializeView = (payload: ViewResponse): View => ({
  id: payload.id,
  name: payload.name,
  propertyIds: payload.property_ids,
  propertyLayouts: payload.property_layouts.map(serializePropertyLayout),
  filters: payload.filters,
  propertyOptions: payload.property_options,
  assignablePropertyId: payload.assignable_property_id,
  numPinnedProperties: payload.num_pinned_properties,
  entityCount: payload.entity_count,
})

export const serializePropertyLayout = (payload: ViewResponse['property_layouts'][number]) => ({
  propertyId: payload.property_id,
  x: payload.x,
  y: payload.y,
  width: payload.width,
  height: payload.height,
})
